import type { SystemRolePermissionId } from '@clockwise/shared';
import { Link } from '@tanstack/react-router';
import { Lock } from 'lucide-react';
import { useMe } from '@/lib/me';
import { landingTitleForPath, resolveLandingPath } from '@/lib/navigation';

interface Props {
  permission: SystemRolePermissionId;
  /** Optional context — what the user was trying to view. */
  resource?: string;
}

/**
 * Full-area "you don't have access" panel. Used at the route level when a
 * user navigates (or is linked) to a page they lack the matching `.view`
 * permission for. The page name and the required permission id are surfaced
 * so the user knows what to ask their admin for. When the user CAN access
 * some other page, we offer a one-click link to it — deep links are
 * user-initiated, so we explain the block rather than silently bouncing.
 */
export function NoAccess({ permission, resource }: Props) {
  const { permissions } = useMe();
  const landing = resolveLandingPath(permissions);
  const landingTitle = landing ? landingTitleForPath(landing) : null;

  return (
    <div className="flex h-full flex-col items-center justify-center gap-3 p-8 text-center">
      <div className="rounded-full bg-muted p-3">
        <Lock className="size-6 text-muted-foreground" aria-hidden />
      </div>
      <h2 className="text-lg font-semibold">No access</h2>
      <p className="max-w-md text-sm text-muted-foreground">
        You don't have permission to view {resource ?? 'this page'}. Ask an admin to grant{' '}
        <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">{permission}</code> on
        your system role.
      </p>
      {landing ? (
        <Link
          to={landing}
          className="text-sm font-medium text-primary underline-offset-4 hover:underline"
        >
          Go to {landingTitle}
        </Link>
      ) : null}
    </div>
  );
}
